import { createStyled, darken } from '@mui/system';
import { deepmerge } from '@mui/utils';
import { createTheme, ThemeOptions } from '@mui/material/styles';
import { alpha } from '@mui/material';

const markerStartWid = 27;
const markerStartHei = 36;
const factor = 1.6;
const outline = 1;
const blur = 0;
const alphaValue = 0.85;
const mobileSideClearance = 15;

const t1 = {
    breakpoints: {
        values: {
            xs: 0,
            xs2: 350,
            sm2: 460,
            sm: 600,
            md: 960,
            lg: 1280,
            xl: 1920,
        },
    },
    searchBar: {
        controlsHeight: 56,
    },
    preferredSpace: 8,
    shape: {
        borderRadius: 8,
    },
    fonts: {
        primary: '\'Viga\', \'Segoe UI\', \'Roboto\', \'Helvetica Neue\', sans-serif',
        secondary: '\'Fredoka One\', \'Segoe UI\', \'Arial Rounded MT Bold\', \'Roboto\', sans-serif',
    },
    spots: {
        transitionTime: '.5s',
        markerStartWid,
        markerStartHei,
        factor,
        markerEndWid: markerStartWid * factor,
        markerEndHei: markerStartHei * factor,
        homeWidth: 60,
        homeHeight: 60,
        homeStrokeWidth: 1,
    },
    palette: {
        primary: {
            main: '#2b5467', // define your primary color here
            contrastText: '#b7e1ef',
            shadow: '#131936',
        },
        secondary: {
            main: '#3a672b', // define your primary color here
            contrastText: '#c1efb7',
            shadow: '#1d3613',
        },
        text: {
            primary: '#333333', // override primary text color
            secondary: '#333333', // override secondary text color
        },
        error: {
            main: '#ff5722', // define your error color here
        },
        warning: {
            main: '#ffc107', // define your warning color here
        },
        info: {
            main: '#2196f3', // define your info color here
        },
        success: {
            main: '#4caf50', // define your success color here
        },
    },
    menubar: {
        height: 100,
    },
    outline,
    blur,
    alpha: alphaValue,
};
const t2 = {
    typography: {
        fontFamily: t1.fonts.primary,
    },
    defaultShadow: (color?: string) => `
    -${t1.outline}px -${t1.outline}px ${t1.blur}px ${alpha(color ?? t1.palette.primary.shadow, t1.alpha)},
    ${t1.outline}px -${t1.outline}px ${t1.blur}px ${alpha(color ?? t1.palette.primary.shadow, t1.alpha)},
    -${t1.outline}px ${t1.outline}px ${t1.blur}px ${alpha(color ?? t1.palette.primary.shadow, t1.alpha)},
    ${t1.outline}px ${t1.outline}px ${t1.blur}px ${alpha(color ?? t1.palette.primary.shadow, t1.alpha)}
  `,
    lightShadow: (color?: string) => `
    -${t1.outline/4}px -${t1.outline/4}px ${t1.blur}px ${alpha(color ?? t1.palette.primary.shadow, t1.alpha)},
    ${t1.outline/4}px -${t1.outline/4}px ${t1.blur}px ${alpha(color ?? t1.palette.primary.shadow, t1.alpha)},
    -${t1.outline/4}px ${t1.outline/4}px ${t1.blur}px ${alpha(color ?? t1.palette.primary.shadow, t1.alpha)},
    ${t1.outline/4}px ${t1.outline/4}px ${t1.blur}px ${alpha(color ?? t1.palette.primary.shadow, t1.alpha)}
  `,
};
const tInt1 = deepmerge<ThemeOptions>(t1, t2);
let kwadHubTheme = createTheme(tInt1);

const t3 = {
    sections: {
        fixedWidth: {
            maxWidth: {
                xs: '100%',
                md: `${kwadHubTheme.breakpoints.values.md - 2 * mobileSideClearance}px`,
            },
            margin: {
                xs: `0 ${mobileSideClearance}px`,
                md: '0 auto',
            },
        },
    },
    typography: {
        h1: {
            fontFamily: kwadHubTheme.fonts.secondary,
            fontWeight: 600,
            letterSpacing: '.1rem',
            color: 'inherit',
            textDecoration: 'none',
            textShadow: kwadHubTheme.defaultShadow(),
            fontSize: '3.8rem',
            [kwadHubTheme.breakpoints.down('lg')]: {
                fontSize: '3.2rem',
            },
            [kwadHubTheme.breakpoints.down('md')]: {
                fontSize: '3rem',
            },
            [kwadHubTheme.breakpoints.down('sm')]: {
                fontSize: '2.8rem',
            },
            [kwadHubTheme.breakpoints.down(400)]: {
                fontSize: '2.2rem',
            },
        },
        h2: {
            fontSize: '2.5rem',
            [`@media (max-width: ${kwadHubTheme.breakpoints.values.sm}px)`]: {
                fontSize: '2.2rem',
                marginBottom: 4,
            },
        },
        h3: {
            fontSize: '2rem',
            [`@media (max-width: ${kwadHubTheme.breakpoints.values.sm}px)`]: {
                fontSize: '1.8rem',
            },
            [`@media (max-width: ${kwadHubTheme.breakpoints.values.xs}px)`]: {
                fontSize: '1.6rem',
            },
        },
        h4: {
            fontSize: '1.5rem',
            [`@media (max-width: ${kwadHubTheme.breakpoints.values.sm}px)`]: {
                fontSize: '1.2rem',
            },
        },
        h6: {
            fontSize: '1.2rem',
            [`@media (max-width: ${kwadHubTheme.breakpoints.values.sm}px)`]: {
                fontSize: '1.1rem',
            },
        },
        body1: {
            fontSize: '1rem',
            // [`@media (max-width: ${kwadHubTheme.breakpoints.values.sm}px)`]: {
            //   fontSize: '0.9rem',
            // }
            em: {
                fontWeight: 'bold',
                textDecoration: 'none',
            },
        },
        body2: {
            fontSize: '1rem',
            [`@media (max-width: ${kwadHubTheme.breakpoints.values.sm}px)`]: {
                fontSize: '.9rem',
            },
        },
        caption: {
            fontSize: '1rem',
            // [`@media (max-width: ${kwadHubTheme.breakpoints.values.sm}px)`]: {
            //   fontSize: '0.9rem',
            // }
        },
        allVariants: {
            fontSize: '1.5rem',
            // [`@media (max-width: ${kwadHubTheme.breakpoints.values.sm}px)`]: {
            //   fontSize: '1.3rem',
            // }
        },
    },
    components: {
        MuiAvatar: {
            styleOverrides: {
                root: {
                    fontSize: '3rem',
                    width: `${kwadHubTheme.menubar.height - 20}px`,
                    height: `${kwadHubTheme.menubar.height - 20}px`,
                    backgroundColor: kwadHubTheme.palette.secondary.main,
                    [kwadHubTheme.breakpoints.up('sm')]: {
                        ['&&']: {
                            boxShadow: t2.defaultShadow(),
                            width: `${kwadHubTheme.menubar.height + 40}px`,
                            height: `${kwadHubTheme.menubar.height + 40}px`,
                        },
                    },
                },
            },
        },
        MuiToolbar: {
            styleOverrides: {
                root: {
                    height: `${kwadHubTheme.menubar.height}px`,
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                paper: {
                    backgroundColor: kwadHubTheme.palette.primary.main,
                    color: kwadHubTheme.palette.primary.contrastText,
                },
            },
        },
        MuiMenuItem: {
            styleOverrides: {
                root: {
                    textShadow: t2.defaultShadow(),
                    fontSize: '1.75rem',
                    fontWeight: 500,
                    padding: kwadHubTheme.spacing(2),
                },
            },
        },
        MuiModal: {
            styleOverrides: {
                root: {
                    [kwadHubTheme.breakpoints.up('sm')]: {
                        ['&&']: {
                            width: '100%',
                        },
                    },
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                container: {
                    maxHeight: 'none',
                    height: 'auto',
                    maxWidth: '100%',
                    width: '800px',
                    margin: '0 auto',
                    minHeight: '100vh',
                },
                paper: {
                    maxWidth: '100%',
                    height: 'auto',
                    overflow: 'auto',
                    borderRadius: '16px',
                    [kwadHubTheme.breakpoints.up('xs')]: {
                        margin:'12px',
                        width: 'calc(100% - 24px)'
                    },
                    [kwadHubTheme.breakpoints.up('sm')]: {
                        margin:'32px',
                        width: 'calc(100% - 64px)'
                    }
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    // borderRadius: "8px",
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    marginTop: `${t1.preferredSpace}px`,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: {
                    padding: '4px 16px',
                },
                message: {
                    width: 'calc(100% - 16px)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    fontSize: '1rem',
                    em: {
                        fontSize: '1.2rem',
                        fontStyle: 'normal',
                        fontWeight: 'bold',
                    },
                },
                icon: {
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: '2.5rem',
                    svg: {
                        verticalAlign: 'center',
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    marginTop: `${t1.preferredSpace}px`,
                    marginBottom: `${t1.preferredSpace}px`,
                },
            },
        },
        MuiFormControlLabel: {
            styleOverrides: {
                label: {
                    fontSize: '1.25em',
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '1.2rem',
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    '&.Mui-selected': {
                        'borderColor': kwadHubTheme.palette.primary.main,
                        'backgroundColor': kwadHubTheme.palette.primary.light,
                        'color': kwadHubTheme.palette.primary.contrastText,
                        '&:hover': {
                            backgroundColor: darken(kwadHubTheme.palette.primary.main, 0.2),
                            borderColor: kwadHubTheme.palette.primary.main,
                        },
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textShadow: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '8px',
                    paddingLeft: `${t1.preferredSpace * 4}px`,
                    paddingRight: `${t1.preferredSpace * 4}px`,
                },
                sizeSmall: {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    paddingLeft: `${t1.preferredSpace * 2}px`,
                    paddingRight: `${t1.preferredSpace * 2}px`,
                },
                sizeMedium: {
                    fontSize: '1rem',
                },
                sizeLarge: {
                    fontSize: '1.125rem',
                    paddingLeft: `${t1.preferredSpace * 6}px`,
                    paddingRight: `${t1.preferredSpace * 6}px`,
                },
                outlinedPrimary: {
                    'borderColor': `${t1.palette.primary.main}`,
                },
                outlined: {
                    'borderWidth': '2px',
                    'borderColor': `${t1.palette.secondary.main}`,
                    '&:hover': {
                        'borderWidth': '2px',
                        'backgroundColor': alpha(t1.palette.primary.main, 0.08),
                    },
                },
            },
        },
    },
};

kwadHubTheme = createTheme(deepmerge(tInt1, t3));

export default kwadHubTheme;
export const styled = createStyled({ defaultTheme: kwadHubTheme });
